import { useMemo, useSyncExternalStore } from 'react';
import {
  persistWritingEnterKeyMode,
  readWritingEnterKeyMode,
  resolveEnterBehavior,
  WRITING_ENTER_KEY_MODE_CHANGE_EVENT,
  type ResolvedEnterBehavior,
  type WritingEnterKeyMode,
} from 'src/modules/shared/utils/writing/enter-key-preference.util';

/**
 * Result returned by the writing Enter-key preference hook.
 */
export interface UseWritingEnterKeyPreferenceResult {
  /** User-selected mode, persisted locally per device/browser. */
  mode: WritingEnterKeyMode;
  /** Concrete Enter behavior resolved for the current environment. */
  resolvedBehavior: ResolvedEnterBehavior;
  /** Updates the local persisted mode immediately. */
  setMode: (mode: WritingEnterKeyMode) => void;
  /** Whether the current behavior still comes from auto detection. */
  isAutoMode: boolean;
}

/**
 * Manages the frontend-only Enter-key writing preference.
 *
 * @returns Persisted mode, resolved behavior, and setter helpers.
 */
const subscribeToWritingEnterKeyMode = (onStoreChange: () => void) => {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  const handleChange = () => onStoreChange();

  window.addEventListener('storage', handleChange);
  window.addEventListener(WRITING_ENTER_KEY_MODE_CHANGE_EVENT, handleChange);

  return () => {
    window.removeEventListener('storage', handleChange);
    window.removeEventListener(
      WRITING_ENTER_KEY_MODE_CHANGE_EVENT,
      handleChange
    );
  };
};

export const useWritingEnterKeyPreference =
  (): UseWritingEnterKeyPreferenceResult => {
    const mode = useSyncExternalStore<WritingEnterKeyMode>(
      subscribeToWritingEnterKeyMode,
      readWritingEnterKeyMode,
      readWritingEnterKeyMode
    );

    const setMode = (nextMode: WritingEnterKeyMode) => {
      persistWritingEnterKeyMode(nextMode);
    };

    const resolvedBehavior = useMemo(() => resolveEnterBehavior(mode), [mode]);

    return {
      mode,
      resolvedBehavior,
      setMode,
      isAutoMode: mode === 'auto',
    };
  };
